import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import figureFormatter from "@/services/utils/figureFormatter";
import { useStore } from "vuex";
import useDate from '@/services/utils/day';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}

const payableAgingPdf = () => {
    const { formatDate } = useDate();
    const store = useStore();
    const { commaFormat } = figureFormatter();

    const userName = store.state.user.user.name;

    const exportToPDF = (company, data, agingPeriods, payload) => {
        const tableBody = formatAgingData(data, agingPeriods);

        const doc = {
            pageSize: 'A4',
            pageOrientation: 'landscape',
            pageMargins: [30, 100, 30, 80],
            header: getHeader(company),
            footer: getFooter(userName),
            content: getContent(tableBody, agingPeriods, payload),
            styles: {
                header: {
                    fontSize: 24
                }
            },
            defaultStyle: {
                color: 'black',
                fontSize: 10,
                font: 'SulaimanLipi'
            },
            info: {
                title: 'Payable Aging'
            }
        }

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    const getContent = (tableBody, agingPeriods, payload) => {
        return [
            {
                text: 'Payable Aging',
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                decoration: 'underline'
            },
            {
                stack: [
                    { text: `Date: ${payload.startDate} - ${payload.endDate}` },
                ]
            },
            {
                style: 'tableExample',
                margin: [0, 10, 0, 0],
                table: {
                    headerRows: 1,
                    widths: getColumnWidths(agingPeriods),
                    body: tableBody
                },
                layout: {
                    fillColor: function (rowIndex) {
                        return (rowIndex === 0) ? '#f3f2f7' : null;
                    },
                    hLineWidth: function () {
                        return 0.5;
                    },
                    vLineWidth: function () {
                        return 0.5;
                    }
                }
            }
        ]
    }

    const getColumnWidths = (agingPeriods) => {
        const widths = ['auto', 'auto', '*', 'auto', 'auto'];
        agingPeriods.forEach(() => widths.push('auto'));
        widths.push('auto');
        return widths;
    }

    const formatAgingData = (data, agingPeriods) => {
        let body = [getTableHead(agingPeriods)];
        body = body.concat(getTableRows(data, agingPeriods));
        body.push(getTotals(data, agingPeriods));
        return body;
    }

    const getTableHead = (agingPeriods) => {
        const head = [
            { text: 'SL NO', bold: true },
            { text: 'CUSTOMER ID', bold: true },
            { text: 'CUSTOMER NAME', bold: true },
            { text: 'MOBILE', bold: true },
            { text: 'CURRENT', bold: true, alignment: 'right' }
        ];
        agingPeriods.forEach((period) => {
            head.push({ text: period.label ? period.label : '', bold: true, alignment: 'right' });
        });
        head.push({ text: 'CLOSING BALANCE', bold: true, alignment: 'right' });
        return head;
    }

    const getTableRows = (data, agingPeriods) => {
        return data.map((item, index) => {
            const row = [
                { text: index + 1 },
                { text: item.serial_no },
                { text: item.name },
                { text: item.phone },
                { text: commaFormat(item.current_balance), alignment: 'right' }
            ];
            agingPeriods.forEach((period) => {
                row.push({ text: commaFormat(item[period.label] || 0), alignment: 'right' });
            });
            row.push({ text: commaFormat(item.closing_balance), alignment: 'right' });
            return row;
        })
    }

    const getTotals = (data, agingPeriods) => {
        const row = [
            { text: 'Total', colSpan: 4, alignment: 'right', bold: true },
            { text: '' },
            { text: '' },
            { text: '' },
            { text: commaFormat(getTotal(data, 'current_balance')), alignment: 'right', bold: true }
        ];
        agingPeriods.forEach((period) => {
            row.push({ text: commaFormat(getTotal(data, period.label)), alignment: 'right', bold: true });
        });
        row.push({ text: commaFormat(getTotal(data, 'closing_balance')), alignment: 'right', bold: true });
        return row;
    }

    const getTotal = (data, key) => {
        return data.reduce((sum, item) => sum + Number(item[key] || 0), 0)
    }

    const getHeader = (company) => {
        return {
            margin: [30, 25, 30, 10],
            columns: [
                {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                },
                {
                    alignment: 'right',
                    stack: [
                        company.name,
                        'Address: ' + company.address,
                        'Phone: ' + company.phone,
                        'E-mail: ' + company.email
                    ]
                }
            ]
        }
    }

    const getFooter = (userName) => {
        return {
            margin: [25, 0],
            columns: [
                {
                    columns: [
                        {
                            width: '90%',
                            alignment: 'left',
                            text: 'N.B: This is a system generated documents and requires no manual signature.',
                            margin: [5, 53, 0, 0],
                            fontSize: 8
                        }
                    ]
                },
                {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        { text: 'Printed by', bold: true },
                        { text: userName },
                        { text: formatDate(new Date()) }
                    ]
                }
            ]
        }
    }

    return {
        exportToPDF
    }
}

export default payableAgingPdf;
